import React from 'react';
import PropTypes from 'prop-types';

import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';

import AddressUnavailableMessage from './address-unavailable-message';
import AddressLoadingIndicator from './address-loading-indicator';
import PromptSearchMessage from './prompt-search-message';

import {
    TypeInput,
    Line1Input,
    Line2Input,
    Line3Input,
    CommentInput,
    SearchInput,
} from './inputs';

import AddressFormPredictions from './address-form-predictions';

import ModalHeader from '../common/modal/header';
import ModalFooter from '../common/modal/footer';

const { CustomerAddress } = Mobi2Go._class;
const { classNames } = Mobi2Go.helpers;

class AddressFormComponent extends React.Component {
    static propTypes = {
        // The address finder search query
        search_query: PropTypes.string.isRequired,

        // Currently address in view
        address: PropTypes.instanceOf(CustomerAddress),

        // Fires whenever a field input changes
        onInputChange: PropTypes.func.isRequired,
        onInputFocus: PropTypes.func.isRequired,
        onInputBlur: PropTypes.func.isRequired,
        onInputKeyDown: PropTypes.func.isRequired,

        // Properties for address type ahead element
        predictions: PropTypes.shape({
            // Force the prediction list not to show
            force_hide: PropTypes.bool,
            highlighted_item: PropTypes.any,
            list: PropTypes.arrayOf(
                PropTypes.shape({
                    description: PropTypes.string,
                    matched_substrings: PropTypes.array,
                    terms: PropTypes.array,
                })
            ),
            onSelect: PropTypes.func,
            onHighlight: PropTypes.func,
            onDelete: PropTypes.func,
        }).isRequired,

        is_address_available: PropTypes.bool.isRequired,
        is_address_availability_loading: PropTypes.bool.isRequired,
        wait_time: PropTypes.number,

        are_predictions_visible: PropTypes.bool.isRequired,

        focused_field: PropTypes.string,
        last_focused_field: PropTypes.string,
        is_mouseover_predictions: PropTypes.bool.isRequired,
        emphasis_address_unavailable_message: PropTypes.bool.isRequired,
        closeModal: PropTypes.func.isRequired,
        t: PropTypes.func.isRequired,
        onSaveClick: PropTypes.func.isRequired,
    };

    static defaultProps = {
        address: null,
        focused_field: null,
        last_focused_field: null,
        wait_time: null,
    };

    constructor() {
        super();

        this.input_events = {
            search_query: this.inputFieldEvents('search_query'),
            type: this.inputFieldEvents('type'),
            name: this.inputFieldEvents('name'),
            specific_number: this.inputFieldEvents('specific_number'),
            street_name: this.inputFieldEvents('street_name'),
            street_number: this.inputFieldEvents('street_number'),
            suburb: this.inputFieldEvents('suburb'),
            post_code: this.inputFieldEvents('post_code'),
            comment: this.inputFieldEvents('comment'),
        };
    }

    inputFieldEvents = field => ({
        onFocus: (...args) => this.onFocus(field, ...args),
        onBlur: (...args) => this.onBlur(field, ...args),
        onChange: (...args) => this.onChange(field, ...args),
        onKeyDown: (...args) => this.onKeyDown(field, ...args),
    });

    onChange = (field, event) => {
        const { onInputChange } = this.props;
        onInputChange(field, event);
    };

    onFocus = (field, event) => {
        const { onInputFocus } = this.props;
        onInputFocus(field, event);
    };

    onBlur = (field, event) => {
        const { onInputBlur } = this.props;
        onInputBlur(field, event);
    };

    onKeyDown = (field, event) => {
        const { onInputKeyDown } = this.props;
        onInputKeyDown(field, event);
    };

    render() {
        const {
            search_query: search_input_events,
            type: type_input_events,
            name: name_input_events,
            specific_number: specific_number_input_events,
            street_name: street_name_input_events,
            street_number: street_number_input_events,
            suburb: suburb_input_events,
            post_code: post_code_input_events,
            comment: comment_input_events,
        } = this.input_events;

        const {
            t,
            address,
            search_query,
            is_address_available,
            is_address_availability_loading,
            wait_time,
            closeModal,
            emphasis_address_unavailable_message,
            predictions,
            are_predictions_visible,
            focused_field,
            onSaveClick,
        } = this.props;

        const show_address_unavailable_message = !!(
            address &&
            (address.is_saved ||
                (address.latitude &&
                    address.longitude &&
                    !is_address_availability_loading &&
                    !is_address_available))
        );
        const show_search_prompt_message =
            !show_address_unavailable_message &&
            !is_address_availability_loading &&
            (!address || !address.latitude || !address.longitude);

        return (
            <div>
                <ModalHeader headerText={t('header')} onClose={closeModal} />

                <div data-form className="m2g-modal-body">
                    <div
                        data-type={address && address.type}
                        className={classNames(
                            'm2g-customer-address-form',
                            'm2g-well',
                            {
                                'm2g-customer-address-form--disabled':
                                    show_address_unavailable_message ||
                                    show_search_prompt_message,
                            }
                        )}
                    >
                        <SearchInput
                            search_query={search_query}
                            is_predictions_list_indicator_visible={
                                predictions.list.length > 0
                            }
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...search_input_events}
                        />

                        {are_predictions_visible && (
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            <AddressFormPredictions {...predictions} />
                        )}

                        <AddressLoadingIndicator
                            show={is_address_availability_loading}
                        />

                        <AddressUnavailableMessage
                            show={show_address_unavailable_message}
                            emphasis={emphasis_address_unavailable_message}
                        />

                        <PromptSearchMessage
                            show={show_search_prompt_message}
                            is_field_focused={focused_field !== null}
                        />

                        {address && !is_address_availability_loading && (
                            <div className="m2g-customer-address-form-animation-container">
                                <TypeInput
                                    label={address.getPropertyLabel('type')}
                                    value={address.type}
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...type_input_events}
                                />

                                <Line1Input
                                    show={address.type !== 'residential'}
                                    label={address.getPropertyLabel('name')}
                                    name={address.name}
                                    name_placeholder={address.getPropertyPlaceholder(
                                        'name'
                                    )}
                                    name_input_events={name_input_events}
                                    specific_number={address.specific_number}
                                    specific_number_placeholder={address.getPropertyPlaceholder(
                                        'specific_number'
                                    )}
                                    specific_number_input_events={
                                        specific_number_input_events
                                    }
                                />

                                <Line2Input
                                    label={address.getPropertyLabel(
                                        'street_name'
                                    )}
                                    show_specific_number={
                                        address.type === 'residential'
                                    }
                                    specific_number={address.specific_number}
                                    specific_number_placeholder={address.getPropertyPlaceholder(
                                        'specific_number'
                                    )}
                                    specific_number_input_events={
                                        specific_number_input_events
                                    }
                                    specific_number_ready_only={
                                        !address.isPropertyEditable(
                                            'specific_number'
                                        )
                                    }
                                    street_number={address.street_number}
                                    street_number_placeholder={address.getPropertyPlaceholder(
                                        'street_number'
                                    )}
                                    street_number_input_events={
                                        street_number_input_events
                                    }
                                    street_number_read_only={
                                        !address.isPropertyEditable(
                                            'street_number'
                                        )
                                    }
                                    street_name={address.street_name}
                                    street_name_placeholder={address.getPropertyPlaceholder(
                                        'street_name'
                                    )}
                                    street_name_input_events={
                                        street_name_input_events
                                    }
                                    street_name_read_only={
                                        !address.isPropertyEditable(
                                            'street_name'
                                        )
                                    }
                                />

                                <Line3Input
                                    label={address.getPropertyLabel('suburb')}
                                    suburb={address.suburb}
                                    suburb_placeholder={address.getPropertyPlaceholder(
                                        'suburb'
                                    )}
                                    suburb_input_events={suburb_input_events}
                                    suburb_read_only={
                                        !address.isPropertyEditable('suburb')
                                    }
                                    post_code={address.post_code}
                                    post_code_placeholder={address.getPropertyPlaceholder(
                                        'post_code'
                                    )}
                                    post_code_input_events={
                                        post_code_input_events
                                    }
                                    post_code_read_only={
                                        !address.isPropertyEditable('post_code')
                                    }
                                />

                                <CommentInput
                                    label={address.getPropertyLabel('comment')}
                                    value={address.comment}
                                    placeholder={address.getPropertyPlaceholder(
                                        'comment'
                                    )}
                                    read_only={
                                        !address.isPropertyEditable('comment')
                                    }
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...comment_input_events}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <ModalFooter>
                    <ModalFooter.Left>
                        {wait_time && (
                            <div
                                className={classNames(
                                    'm2g-customer-address-wait-time',
                                    {
                                        'm2g-customer-address-wait-time--hidden': !is_address_available,
                                    }
                                )}
                            >
                                <span className="m2g-customer-address-wait-time-icon m2g-icon m2g-icon--large m2g-icon--clock" />
                                <div className="m2g-customer-address-wait-time-content">
                                    <strong className="m2g-customer-address-wait-time-content-minutes">
                                        {t('estimated_delivery_time', {
                                            time: wait_time,
                                        })}
                                    </strong>
                                    <div className="m2g-customer-address-wait-time-content-text">
                                        {t('estimated_delivery_time_label')}
                                    </div>
                                </div>
                            </div>
                        )}
                    </ModalFooter.Left>
                    <ModalFooter.Right>
                        <span
                            data-spinner
                            className="m2g-customer-address-confirm-spinner m2g-icon m2g-icon--spinner m2g-icon--medium m2g-hidden"
                        />

                        <button
                            data-confirm
                            type="submit"
                            onClick={onSaveClick}
                            className={classNames(
                                'm2g-customer-address-confirm-button',
                                'm2g-button',
                                'm2g-button--primary',
                                {
                                    'm2g-button--disabled': !is_address_available,
                                }
                            )}
                        >
                            {t('ok_button')}
                        </button>
                    </ModalFooter.Right>
                </ModalFooter>
            </div>
        );
    }
}

export default forwardRefWithTranslation('customer.address.uber-direct')(
    AddressFormComponent
);
